// Interner Chat (1:1 + Gruppen) — ein Bestand für die ganze App, damit die Pille den Ungelesen-Punkt
// zeigen kann, ohne dass die Chat-Ansicht offen ist. Live über Supabase Realtime.
//
// Der Bestand wird wöchentlich geleert (Cron „chat-aufraeumen-woechentlich"), deshalb wird
// er bewusst KOMPLETT im Speicher gehalten — Paginierung wäre Aufwand für nichts.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase/client';
import {
  gruppenName, gruppiereAlles, kollegeName, vorschauText, zaehleUngelesenGesamt,
  type ChatAnhang, type ChatGruppe, type ChatNachricht, type Kollege, type Unterhaltung,
} from '../lib/chat/nachrichten';
import {
  ladeGruppen, ladeKollegen, ladeNachrichten, markiereGelesen, sendeNachricht, loescheNachricht,
  setzeGruppenLesestand,
} from '../lib/chat/api';

interface ChatContextValue {
  meineId: string | null;
  kollegen: Kollege[];
  nachrichten: ChatNachricht[];
  gruppen: ChatGruppe[];
  unterhaltungen: Unterhaltung[];
  ungelesen: number;
  senden: (ziel: { art: 'person' | 'gruppe'; id: string }, text: string, bild?: { path: string; name: string } | null, anhang?: ChatAnhang | null) => Promise<boolean>;
  gelesen: (ziel: { art: 'person' | 'gruppe'; id: string }) => Promise<void>;
  loeschen: (n: ChatNachricht) => Promise<void>;
  neuLaden: () => Promise<void>;
  gruppenNeuLaden: () => Promise<void>;
  /**
   * Zuletzt LIVE eingegangene fremde Nachricht. Die Pille blendet sie seit 17.08.2026
   * NICHT mehr ein (Chat meldet sich in der Kopfleiste, siehe ChatGlocke) — der Wert
   * bleibt als Signal für künftige Melder erhalten.
   * Wird nur von Realtime gesetzt, nie vom Erstabruf (sonst käme beim Seitenaufruf eine
   * Einblendung für längst gelesene Nachrichten). Gilt für 1:1 UND Gruppen; `ort` trägt
   * bei Gruppen deren Namen, damit die Vorschau zeigt, WO geschrieben wurde. `ziel` macht
   * die Einblendung anklickbar.
   */
  letzterEingang: {
    id: string;
    von: string;
    ort: string | null;
    vorschau: string;
    ziel: { art: 'person' | 'gruppe'; id: string };
  } | null;
  /**
   * Von außen angefordertes Gespräch (Klick auf die Vorschau in der Pille). Die Chat-Seite
   * übernimmt es und meldet sich mit `zielGeoeffnet` zurück — sonst würde dieselbe
   * Anforderung bei jedem Rendern erneut greifen und eine eigene Auswahl überschreiben.
   */
  zuOeffnendesZiel: { art: 'person' | 'gruppe'; id: string } | null;
  oeffneZiel: (ziel: { art: 'person' | 'gruppe'; id: string }) => void;
  zielGeoeffnet: () => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

const LEER: ChatContextValue = {
  meineId: null, kollegen: [], nachrichten: [], gruppen: [], unterhaltungen: [], ungelesen: 0,
  senden: async () => false, gelesen: async () => {}, loeschen: async () => {}, neuLaden: async () => {},
  gruppenNeuLaden: async () => {},
  letzterEingang: null,
  zuOeffnendesZiel: null, oeffneZiel: () => {}, zielGeoeffnet: () => {},
};

export const useChat = (): ChatContextValue => useContext(ChatContext) ?? LEER;

export const ChatProvider: React.FC<{ userId: string | null; children: React.ReactNode }> = ({ userId, children }) => {
  const [kollegen, setKollegen] = useState<Kollege[]>([]);
  const [nachrichten, setNachrichten] = useState<ChatNachricht[]>([]);
  const [gruppen, setGruppen] = useState<ChatGruppe[]>([]);
  const [letzterEingang, setLetzterEingang] = useState<ChatContextValue['letzterEingang']>(null);
  const [zuOeffnendesZiel, setZuOeffnendesZiel] = useState<ChatContextValue['zuOeffnendesZiel']>(null);
  // Namen für die Vorschau ohne Neuaufbau des Realtime-Kanals (der Effekt hängt nur an userId).
  const kollegenRef = useRef<Kollege[]>([]);
  kollegenRef.current = kollegen;
  // Dito für Gruppennamen: der Gruppen-Kanal wird nur bei geänderter Gruppenmenge neu
  // aufgebaut, kennt sonst aber den Stand von damals.
  const gruppenRef = useRef<ChatGruppe[]>([]);
  gruppenRef.current = gruppen;

  const oeffneZiel = useCallback((ziel: { art: 'person' | 'gruppe'; id: string }) => {
    setZuOeffnendesZiel(ziel);
  }, []);
  const zielGeoeffnet = useCallback(() => setZuOeffnendesZiel(null), []);

  const neuLaden = useCallback(async () => {
    if (!userId) { setNachrichten([]); return; }
    setNachrichten(await ladeNachrichten(userId));
  }, [userId]);

  const gruppenNeuLaden = useCallback(async () => {
    if (!userId) { setGruppen([]); return; }
    setGruppen(await ladeGruppen(userId));
  }, [userId]);

  useEffect(() => {
    if (!userId) { setKollegen([]); setNachrichten([]); setGruppen([]); return; }
    void (async () => {
      const [k, n, g] = await Promise.all([ladeKollegen(userId), ladeNachrichten(userId), ladeGruppen(userId)]);
      setKollegen(k);
      setNachrichten(n);
      setGruppen(g);
    })();
  }, [userId]);

  // Live: eingehende Nachrichten an mich sowie Lesestempel/Löschungen im eigenen Verlauf.
  // Zwei Filter sind nötig, weil postgres_changes nur EINE Spalte je Filter vergleicht.
  useEffect(() => {
    if (!userId) return;
    const einfuegen = (roh: unknown) => {
      const neu = roh as ChatNachricht;
      setNachrichten((prev) => (prev.some((n) => n.id === neu.id) ? prev : [neu, ...prev]));
    };
    const kanal = supabase
      .channel(`chat-${userId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_nachrichten', filter: `empfaenger_id=eq.${userId}` },
        (p) => {
          const neu = p.new as ChatNachricht;
          einfuegen(neu);
          const von = kollegenRef.current.find((k) => k.id === neu.absender_id);
          setLetzterEingang({
            id: neu.id, von: kollegeName(von), ort: null, vorschau: vorschauText(neu),
            ziel: { art: 'person', id: neu.absender_id },
          });
        })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_nachrichten', filter: `absender_id=eq.${userId}` },
        (p) => einfuegen(p.new))
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'chat_nachrichten', filter: `absender_id=eq.${userId}` },
        (p) => {
          const akt = p.new as ChatNachricht;
          setNachrichten((prev) => prev.map((n) => (n.id === akt.id ? akt : n)));
        })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'chat_nachrichten' },
        (p) => {
          const weg = p.old as { id?: string };
          if (weg?.id) setNachrichten((prev) => prev.filter((n) => n.id !== weg.id));
        })
      .subscribe();
    return () => { void supabase.removeChannel(kanal); };
  }, [userId]);

  // Live für Gruppen: EIGENER Effekt, weil postgres_changes je Filter nur eine Spalte
  // vergleicht — „eine meiner Gruppen" lässt sich nicht in einem Filter ausdrücken, deshalb
  // ein Kanal je Gruppen-ID statt eines gemeinsamen Filters wie beim 1:1-Kanal oben.
  // Kanäle nur neu aufbauen, wenn sich die Gruppen-Menge wirklich ändert
  // (ein neues Array bei jedem Laden würde sonst dauernd neu abonnieren).
  const gruppenIds = useMemo(() => gruppen.map((g) => g.id).sort().join(','), [gruppen]);

  useEffect(() => {
    if (!userId || !gruppenIds) return;
    const ids = gruppenIds.split(',');
    const kanaele = ids.map((gid) =>
      supabase
        .channel(`chat-gruppe-${gid}`)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_nachrichten', filter: `gruppe_id=eq.${gid}` },
          (p) => {
            const neu = p.new as ChatNachricht;
            setNachrichten((prev) => (prev.some((n) => n.id === neu.id) ? prev : [neu, ...prev]));
            // Eigene Nachrichten kommen über denselben Kanal zurück — sie dürfen keine
            // Vorschau auslösen, sonst meldet die Pille einem jede eigene Zeile.
            if (neu.absender_id === userId) return;
            const von = kollegenRef.current.find((k) => k.id === neu.absender_id);
            const gruppe = gruppenRef.current.find((g) => g.id === gid);
            setLetzterEingang({
              id: neu.id, von: kollegeName(von),
              ort: gruppe ? gruppenName(gruppe) : null,
              vorschau: vorschauText(neu),
              ziel: { art: 'gruppe', id: gid },
            });
          })
        .subscribe());
    return () => { for (const k of kanaele) void supabase.removeChannel(k); };
  }, [userId, gruppenIds]);

  // Live für Mitgliedschaften: EIGENER Kanal (nicht Teil des Effekts oben), weil er auf
  // Änderungen reagieren muss, die die eigene Gruppen-Menge selbst verändern — Aufnahme in
  // eine neue Gruppe, Entfernen, Team-Löschung. Hängt bewusst NUR an userId/gruppenNeuLaden,
  // nicht an gruppen/gruppenIds: sonst würde jedes Neuladen der Gruppen den Kanal neu
  // aufbauen (Kanal löst sich selbst aus, baut sich neu auf, löst sich wieder aus, …).
  // Ohne dieses Abo sieht eine offene Sitzung neue/entfernte Gruppen erst nach Neuladen.
  //
  // team_members gehört dazu: Team-Chats beziehen ihre Mitglieder aus dieser Tabelle und
  // NICHT aus chat_gruppen_mitglieder (ladeGruppen in lib/chat/api.ts). Ohne sie blieb eine
  // Aufnahme ins oder ein Austritt aus dem Team in offenen Sitzungen unsichtbar. Die Tabelle
  // musste dafür in die Realtime-Publikation (Migration 2026-08-25-team-members-realtime).
  useEffect(() => {
    if (!userId) return;
    const kanal = supabase
      .channel(`chat-mitgliedschaft-${userId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_gruppen' }, () => void gruppenNeuLaden())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_gruppen_mitglieder' }, () => void gruppenNeuLaden())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'team_members' }, () => void gruppenNeuLaden())
      .subscribe();
    return () => { void supabase.removeChannel(kanal); };
  }, [userId, gruppenNeuLaden]);

  const senden: ChatContextValue['senden'] = useCallback(async (ziel, text, bild, anhang) => {
    if (!userId) return false;
    if (!text.trim() && !bild && !anhang) return false;
    const neu = await sendeNachricht({
      absenderId: userId,
      empfaengerId: ziel.art === 'person' ? ziel.id : null,
      gruppeId: ziel.art === 'gruppe' ? ziel.id : null,
      text, bildPath: bild?.path || null, bildName: bild?.name || null, anhang: anhang || null,
    });
    if (!neu) return false;
    // Sofort einsetzen — der Realtime-Rücklauf wird per id-Prüfung verworfen.
    setNachrichten((prev) => (prev.some((n) => n.id === neu.id) ? prev : [neu, ...prev]));
    return true;
  }, [userId]);

  const gelesen = useCallback(async (ziel: { art: 'person' | 'gruppe'; id: string }) => {
    if (!userId) return;
    if (ziel.art === 'gruppe') {
      const jetzt = new Date().toISOString();
      // Sofort im Speicher setzen, sonst blinkt der Zähler bis zum nächsten Laden.
      setGruppen((prev) => prev.map((g) => (g.id === ziel.id ? { ...g, gelesen_bis: jetzt } : g)));
      await setzeGruppenLesestand(userId, ziel.id);
      return;
    }
    const jetzt = new Date().toISOString();
    setNachrichten((prev) => prev.map((n) =>
      n.absender_id === ziel.id && n.empfaenger_id === userId && !n.gelesen_am ? { ...n, gelesen_am: jetzt } : n));
    await markiereGelesen(userId, ziel.id);
  }, [userId]);

  const loeschen = useCallback(async (n: ChatNachricht) => {
    const ok = await loescheNachricht(n);
    if (ok) setNachrichten((prev) => prev.filter((x) => x.id !== n.id));
  }, []);

  const unterhaltungen = useMemo(
    () => (userId ? gruppiereAlles(nachrichten, userId, gruppen) : []),
    [nachrichten, userId, gruppen],
  );
  const ungelesen = useMemo(
    () => (userId ? zaehleUngelesenGesamt(nachrichten, userId, gruppen) : 0),
    [nachrichten, userId, gruppen],
  );

  const wert = useMemo<ChatContextValue>(() => ({
    meineId: userId, kollegen, nachrichten, gruppen, unterhaltungen, ungelesen,
    senden, gelesen, loeschen, neuLaden, gruppenNeuLaden,
    letzterEingang, zuOeffnendesZiel, oeffneZiel, zielGeoeffnet,
  }), [userId, kollegen, nachrichten, gruppen, unterhaltungen, ungelesen, senden, gelesen, loeschen, neuLaden, gruppenNeuLaden, letzterEingang, zuOeffnendesZiel, oeffneZiel, zielGeoeffnet]);

  return <ChatContext.Provider value={wert}>{children}</ChatContext.Provider>;
};
